import { createWebHistory, createRouter } from "vue-router";
import * as Sentry from "@sentry/vue";
import routes from "@/routes";
import store from "@/store";

const router = createRouter({
  history: createWebHistory(),
  routes,
});

// A deploy can leave a tab requesting a chunk hash that no longer exists. A missing chunk
// returns the SPA fallback (200 text/html), not a 404, so browsers reject the module with a
// MIME-type error ("not a valid JavaScript MIME type") rather than a fetch failure — and each
// engine words the failure differently. Match all known phrasings so detection works regardless
// of browser or whether the server 404s vs. serves the fallback. Also used by the Sentry
// beforeSend filter in app.js to drop these as raw unhandled exceptions.
export const isStaleAssetError = (message) => /dynamically imported module|module script|Unable to preload|valid JavaScript MIME type/i.test(message);

// Stale-asset recovery: reload to the given path to pull a fresh index + assets. The guard
// records the release the page was running and when it reloaded. A failure on that same release
// inside the window means the reload brought nothing newer, so it is reported instead; a new
// release re-arms recovery straight away, and the window expiring lets a later deploy (or a
// network blip that triggered the first reload) recover on the same release too. Builds without a
// release get the plain window. The guard is never cleared on success: the reload's own first
// navigation completes before its lazy components import, so clearing there re-arms a loop for a
// chunk that fails every time. Without storage there is no guard, so nothing reloads. Returns
// true when a reload is under way, false when the failure should be reported. The latch keeps a
// second failure in the same tick (two lazy imports on one page) from reporting while the first
// one reloads.
const ASSET_RETRY_KEY = "asset-retry";
const ASSET_RETRY_WINDOW_MS = 5 * 60 * 1000;
const release = __APP_RELEASE__ || "";
let reloading = false;

const readAssetRetry = () => {
  try {
    return JSON.parse(sessionStorage.getItem(ASSET_RETRY_KEY));
  } catch {
    return null;
  }
};

const writeAssetRetry = () => {
  try {
    sessionStorage.setItem(ASSET_RETRY_KEY, JSON.stringify({ release, at: Date.now() }));
    return true;
  } catch {
    return false;
  }
};

const recoverStaleAsset = (fullPath) => {
  if (reloading) return true;
  const guard = readAssetRetry();
  const elapsed = Date.now() - Number(guard?.at);
  const retried = guard?.release === release && elapsed >= 0 && elapsed < ASSET_RETRY_WINDOW_MS;
  if (retried || !writeAssetRetry()) return false;
  reloading = true;
  window.location = fullPath;
  return true;
};

// Route navigation errors. A failed lazy route chunk surfaces here with its target path, so the
// reload lands where the user was going. Exhausted retries report under a fixed message (the
// error text names hashed chunk files that change every deploy); any other router error is a
// real exception and reports as one, grouped by its own stack.
let routeHandledError = null;
router.onError((error, to) => {
  routeHandledError = error;
  if (!error.message) return;
  if (!isStaleAssetError(error.message)) {
    Sentry.captureException(error, { extra: { path: to.fullPath } });
    return;
  }
  if (recoverStaleAsset(to.fullPath)) return;
  Sentry.captureMessage("Router error", { extra: { path: to.fullPath, error: error.message } });
});

// Preload failures outside a route navigation (a lazily imported component, editor or
// stylesheet) get the same reload, to the current page; any other import failure (a chunk the
// browser can't parse) is reported, since a reload wouldn't fix it and loses unsaved input.
// Vite raises this event for every failed import, route chunks included, before rethrowing the
// same error into router.onError a few microtasks later, so the listener defers a tick and
// stands down when the router took that error: the router reloads to the target path rather
// than the page being left, and reports the failure itself.
window.addEventListener("vite:preloadError", (event) => {
  const payload = event?.payload;
  const error = String(payload || "");
  const path = window.location.pathname + window.location.search;
  setTimeout(() => {
    if (payload && payload === routeHandledError) return;
    if (isStaleAssetError(error) && recoverStaleAsset(path)) return;
    Sentry.captureMessage("Vite preload error", { extra: { path, error } });
  });
});

router.beforeEach((to, from) => {
  // set page title
  document.title = to.meta.title;

  // see if user is logged in.
  const { loggedIn } = store.state.auth;

  // handle order routing to public invoice
  if (!loggedIn && to.path.includes("/orders/")) {
    if (to.path.includes("receiving/")) {
      // let order_uuid = to.params.order_uuid;
      const orderUUID = /[^/]*$/.exec(to.path)[0];
      return { path: `/invoice/${orderUUID}?order_type=receiving`, replace: true, query: { redirect: to.fullPath } };
    } else if (to.path.includes("shipping/")) {
      const orderUUID = /[^/]*$/.exec(to.path)[0];
      return { path: `/invoice/${orderUUID}?order_type=shipping`, replace: true, query: { redirect: to.fullPath } };
    }
  } else if (to.matched.some((record) => record.meta.requiresAuth) && !loggedIn && to.path !== "/login") {
    // check if allowed to see route
    return { path: "/login", query: { redirect: to.fullPath } };
  } else if (
    // Redirect unverified users to verification page, preserving destination.
    // When a user clicks a verification link (e.g. from a storefront), the server
    // verifies their email and redirects to the target path. But the Vue app boots
    // with stale auth state (email_verified_at still null), so this guard intercepts.
    // VerifyEmail.vue fetches fresh user data, sees the email is now verified, and
    // redirects to the preserved destination via the `redirect` query param.
    loggedIn &&
    store.state.auth.user.email_verified_at === null &&
    !["/verify-email", "/logout"].includes(to.path)
  ) {
    return { path: "/verify-email", query: { redirect: to.fullPath } };
  } else if (
    // prevent access to main company pages if no main current_company_id exists for user
    store.state.auth.user.current_company_id === null &&
    !["/companies", "/profile", "/logout", "/security", "/verify-email"].includes(to.path)
  ) {
    return { path: "/companies", query: { redirect: to.fullPath } };
  } else if (
    // Partner (integrator) companies are API-only token owners with no operational app.
    // Confine them to the partner stub, plus account-level pages needed to switch/leave.
    // Kiosk staff are never confined (mirrors the backend); when impersonating a partner
    // the current user is the partner (not a kiosk user), so they still see the stub and
    // exit via "Stop Impersonation" in the header.
    loggedIn &&
    !store.state.auth.user.isKioskUser &&
    store.state.auth.user.current_company &&
    store.state.auth.user.current_company.subscription === "partner" &&
    !["/partner", "/companies", "/profile", "/logout", "/security", "/verify-email"].includes(to.path)
  ) {
    return { path: "/partner" };
  }
});

export default router;
